import * as React from 'react';
import { PencilSimpleLineIcon, UploadSimpleIcon } from '@phosphor-icons/react';
import { cn } from '@/utils/utils';
import { DISABLED, FOCUS } from '@/components/ui/recipes';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { LoadingSpinner } from '@/common/LoadingSpinner';

interface ImageButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /** What clicking it does, as its accessible name and tooltip (Change cover image). */
  label: string;
  /** The image, video or placeholder art it shows; it fills the button. Left out, the button is an empty upload target. */
  children?: React.ReactNode;
  /** The empty upload target's words (Upload, Click to upload video). */
  emptyLabel?: string;
  /** A round button (an avatar) instead of the rounded rectangle (a cover, a tile). */
  round?: boolean;
  /** For a thumbnail that picks what it shows: whether it is the one picked. Left out, the button changes its image. */
  selected?: boolean;
  /** The icon hover shows over an image it changes; a pencil by default. */
  hint?: React.ReactNode;
  /** A required image that is missing or wrong. */
  invalid?: boolean;
  /** The image is uploading or saving. */
  busy?: boolean;
  /** Keeps the upload target's dashed frame and fill round its image, as the publish flow's covers do. */
  framed?: boolean;
}

/** An image that is itself the control: a cover, a photo or a thumbnail whose picture changes or picks it. */
export const ImageButton = React.forwardRef<HTMLButtonElement, ImageButtonProps>(
  ({ label, children, emptyLabel = 'Upload', round = false, selected, hint, invalid = false, busy = false, framed = false, className, type = 'button', ...props }, ref) => {
    const empty = children == null;
    const picks = selected !== undefined;
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            ref={ref}
            type={type}
            aria-label={label}
            aria-pressed={picks ? selected : undefined}
            aria-busy={busy || undefined}
            className={cn(
              'group relative block overflow-hidden font-lureo-font-ui transition-colors',
              // An upload target's dashed frame takes the upload weight; an image alone keeps the medium line for its states
              empty || framed ? 'border-lureo-upload-line-width' : 'border-lureo-border-width-medium',
              round ? 'rounded-full' : picks ? 'rounded-lureo-radius-lg' : 'rounded-lureo-radius',
              // An image shows on what it sits on, so art with clear parts (the squiggle) has no box behind it
              empty && 'flex flex-col items-center justify-center gap-1.5 border-dashed bg-lureo-neutral-subtle text-lureo-ink-subtlest hover:border-lureo-brand-ink hover:text-lureo-brand-ink',
              framed && !empty && 'border-dashed bg-lureo-neutral-subtle hover:border-lureo-brand-ink',
              invalid
                ? 'border-lureo-danger-ink'
                : empty || framed
                  ? 'border-lureo-line-strong'
                  : selected
                    ? 'border-transparent ring-2 ring-lureo-brand-ink ring-offset-2'
                    : picks
                      ? 'border-transparent opacity-70 hover:opacity-90'
                      : 'border-transparent',
              FOCUS,
              DISABLED,
              className,
            )}
            {...props}
          >
            {empty ? (
              !busy && (
                <>
                  <UploadSimpleIcon className="h-6 w-6" />
                  <span className="text-lureo-12 leading-[1.3]">{emptyLabel}</span>
                </>
              )
            ) : (
              <>
                {children}
                {!picks && (
                  <span aria-hidden className="pointer-events-none absolute inset-0 flex items-center justify-center bg-lureo-emphasis/0 text-lureo-on-emphasis transition-colors group-hover:bg-lureo-emphasis/30 group-focus-visible:bg-lureo-emphasis/30 [&_svg]:h-6 [&_svg]:w-6">
                    <span className="opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">{hint ?? <PencilSimpleLineIcon />}</span>
                  </span>
                )}
              </>
            )}
            {busy && (
              <span className="absolute inset-0 flex items-center justify-center bg-lureo-surface/60">
                <LoadingSpinner size="md" />
              </span>
            )}
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom">{label}</TooltipContent>
      </Tooltip>
    );
  },
);
ImageButton.displayName = 'ImageButton';
